import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  InventoryMap,
  SupplierMap,
  SupplierProduct,
  ScanMode,
  ScannerState,
} from '../components/types';
import { supabaseService } from '../services/supabaseService';
import { generateInternalEan } from '../utils/eanGenerator';
import { getUserFriendlyError } from '../utils/errorHelper';
import { useNetworkStatus } from './useNetworkStatus';

interface UseScannerLogicProps {
  inventoryData: InventoryMap;
  setInventoryData: React.Dispatch<React.SetStateAction<InventoryMap>>;
  supplierData: SupplierMap;
  setSupplierData: React.Dispatch<React.SetStateAction<SupplierMap>>;
  savedState: ScannerState;
  setSavedState: React.Dispatch<React.SetStateAction<ScannerState>>;
  allowedModes: ScanMode[];
  hasPosModule?: boolean;
  onCheckoutStart?: () => void;
  onCheckoutComplete?: () => void;
  currentTerminalName: string;
}

interface OutOfStockError {
  ean: string;
  name: string;
}

interface RecentTransaction {
  id: string;
  ean: string;
  quantity_change: number;
  price: number;
  selling_price: number;
  product_name: string;
  transaction_type: string;
  created_at: string;
}

// Audio feedback utilities
const playBeep = (frequency: number, duration: number, type: 'sine' | 'square' = 'sine'): void => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
};

const playSuccessSound = (): void => playBeep(800, 0.1, 'sine');
const playErrorSound = (): void => {
  playBeep(200, 0.15, 'square');
  setTimeout(() => playBeep(150, 0.15, 'square'), 50);
};

export const useScannerLogic = ({
  inventoryData = {},
  setInventoryData,
  supplierData = {},
  setSupplierData,
  savedState,
  setSavedState,
  allowedModes = ['COUNT'],
  hasPosModule = false,
  onCheckoutStart,
  onCheckoutComplete,
  currentTerminalName,
}: UseScannerLogicProps) => {
  // 🔥 ATOMBIZTOSAN MŰKÖDŐ NETWORK STATUS
  const networkStatus = useNetworkStatus();

  // Safe defaults
  const safeInventoryData = inventoryData || {};
  const safeSupplierData = supplierData || {};
  const safeAllowedModes =
    Array.isArray(allowedModes) && allowedModes.length > 0 ? allowedModes : ['COUNT'];
  const safeCart = Array.isArray(savedState?.cart) ? savedState.cart : [];

  // Local state
  const [isMuted, setIsMuted] = useState(() => {
    const saved = localStorage.getItem('scanner_audio_muted');
    return saved === 'true';
  });
  const [isRapidMode, setIsRapidMode] = useState(() => {
    const saved = localStorage.getItem('scanner_rapid_mode');
    return saved === 'true';
  });

  const [outOfStockError, setOutOfStockError] = useState<OutOfStockError | null>(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [lastTransaction, setLastTransaction] = useState<{
    items: Array<{
      ean: string;
      name: string;
      quantity: number;
      price: number;
      total: number;
      vatRate?: number;
    }>;
    totalAmount: number;
    paymentType: 'CASH' | 'INVOICE' | 'CARD' | 'OTHER';
    transactionId?: string;
    date: Date;
    sellerName?: string;
    discountPercent?: number;
    discountAmount?: number;
  } | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSuspendingCart, setIsSuspendingCart] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendNotes, setSuspendNotes] = useState('');
  const [transactionNote, setTransactionNote] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [identifiedSellerName, setIdentifiedSellerName] = useState<string>('');
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [pendingWeighableItem, setPendingWeighableItem] = useState<{
    ean: string;
    product: SupplierProduct;
    inventory: { quantity: number; scannedAt: Date };
  } | null>(null);
  const [pendingTransactions, setPendingTransactions] = useState<
    Array<{
      ean: string;
      name: string;
      quantity: number;
      mode: 'RETURN' | 'SCRAP' | 'INTERNAL';
      unit: string;
    }>
  >([]);
  const [showTransactionConfirmationModal, setShowTransactionConfirmationModal] = useState(false);
  const [transactionUser, setTransactionUser] = useState<string>('');

  // Refs
  const mainInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Removed lookedUpEansRef - we now always fetch fresh data in handleScan
  const isProcessingRef = useRef<boolean>(false);

  // Unified state update helper
  const updateSavedState = useCallback(
    (updates: Partial<ScannerState>) => {
      setSavedState((prev) => ({ ...prev, ...updates }));
    },
    [setSavedState]
  );

  // Extract state values
  const {
    barcodeInput = '',
    lastScanned = null,
    newName = '',
    newPrice = '',
    newSellingPrice = '',
    newPurchaseNet,
    newPurchaseGross,
    newSellingNet,
    newSellingGross,
    newVatRate = 27,
    newMinQuantity = '',
    newIsWeighable = false,
    newUnitWeight = '',
    newUnit = 'db',
    scanMode = safeAllowedModes[0] as ScanMode,
    transactionQty = 1,
    isCheckoutModalOpen = false,
    checkoutStep = 'SELECT_TYPE',
    selectedPaymentType = 'CASH',
    discountPercent = 0,
    pendingAdjustment = null,
  } = savedState || {};

  // Validate scanMode
  useEffect(() => {
    if (scanMode && !safeAllowedModes.includes(scanMode)) {
      updateSavedState({ scanMode: safeAllowedModes[0] as ScanMode });
    }
  }, [scanMode, safeAllowedModes, updateSavedState]);

  // Load recent transactions
  useEffect(() => {
    const loadRecentTransactions = async () => {
      try {
        const transactions = await supabaseService.getRecentTransactions(5);
        setRecentTransactions(Array.isArray(transactions) ? transactions : []);
      } catch (error) {
        console.error('Error loading recent transactions:', error);
        setRecentTransactions([]);
      }
    };
    loadRecentTransactions();
  }, [inventoryData]);

  /**
   * Attempts to focus the main scanner input.
   * STRICT GUARD: Kills focus attempts if Modal is open OR User is typing elsewhere.
   */
  const attemptFocusMainInput = useCallback(() => {
    // 1. GLOBAL KILL SWITCH: If the checkout/confirmation modal is open, NEVER steal focus.
    if (savedState.isCheckoutModalOpen) {
      return;
    }

    // 2. ACTIVE ELEMENT CHECK: If user is typing in a textarea or another input, DO NOT disturb.
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      (active.tagName === 'TEXTAREA' ||
        (active.tagName === 'INPUT' && active !== mainInputRef.current))
    ) {
      return;
    }

    mainInputRef.current?.focus();
  }, [savedState.isCheckoutModalOpen]);

  // Aggressive focus management
  useEffect(() => {
    // 1. CRITICAL FIX: If the checkout modal is open, STOP HERE.
    // Do not set any timers. Do not focus anything. Just let the user type.
    if (isCheckoutModalOpen) {
      return;
    }

    // 2. Handle unknown product focus (Name input)
    if (lastScanned) {
      const isKnown = !!safeSupplierData[lastScanned];
      if (!isKnown) {
        // If user is typing in the name input, DO NOT steal focus
        const active = document.activeElement;
        if (active === nameInputRef.current) {
          return; // User is typing, leave them alone
        }

        // If no name entered yet, focus the name input
        if (!newName) {
          const timer = setTimeout(() => {
            nameInputRef.current?.focus();
          }, 50);
          return () => clearTimeout(timer);
        }

        // If name is being entered (newName exists), don't focus main input
        // Let the user continue typing
        return;
      }
    }

    // 3. Standard Scanner Focus (Main Input)
    const timer = setTimeout(() => {
      // Only focus if modal is still closed
      if (!isCheckoutModalOpen) {
        mainInputRef.current?.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [
    // Dependencies
    lastScanned,
    safeSupplierData,
    newName,
    scanMode,
    isRapidMode,
    isCheckoutModalOpen, // This must be here to re-run when modal opens/closes
  ]);

  // Audio feedback
  const playAudio = useCallback(
    (type: 'success' | 'error') => {
      if (!isMuted) {
        if (type === 'success') playSuccessSound();
        else playErrorSound();
      }
    },
    [isMuted]
  );

  const toggleMute = useCallback(() => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    localStorage.setItem('scanner_audio_muted', String(newMutedState));
  }, [isMuted]);

  // Rapid mode toggle
  const toggleRapidMode = useCallback(() => {
    const newRapidMode = !isRapidMode;
    setIsRapidMode(newRapidMode);
    localStorage.setItem('scanner_rapid_mode', String(newRapidMode));
  }, [isRapidMode]);

  // Global keyboard shortcut listener
  useEffect(() => {
    const handleToggleRapidMode = () => toggleRapidMode();
    window.addEventListener('toggleRapidMode', handleToggleRapidMode as EventListener);
    return () =>
      window.removeEventListener('toggleRapidMode', handleToggleRapidMode as EventListener);
  }, [toggleRapidMode]);

  // Focus management timeout refs (memory leak prevention)
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nameInputTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generateEanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup all timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
      if (nameInputTimeoutRef.current) {
        clearTimeout(nameInputTimeoutRef.current);
      }
      if (generateEanTimeoutRef.current) {
        clearTimeout(generateEanTimeoutRef.current);
      }
    };
  }, []);

  // Focus management helper
  const returnFocusToMainInput = useCallback(() => {
    // Clear any pending focus timeout to prevent memory leak
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }

    focusTimeoutRef.current = setTimeout(() => {
      attemptFocusMainInput();
      focusTimeoutRef.current = null;
    }, 50);
  }, [attemptFocusMainInput]);

  // Update newUnit helper function
  const updateNewUnit = useCallback(
    (val: 'db' | 'm' | 'kg' | string) => {
      updateSavedState({ newUnit: (val as 'db' | 'm' | 'kg') || 'db' });
    },
    [updateSavedState]
  );

  // Check stock before subtraction
  const checkStock = useCallback(
    (
      ean: string,
      requestedQty: number = 1,
      productData?: { supplier?: SupplierProduct; inventory?: { quantity: number } }
    ): boolean => {
      const isSubtractionMode =
        scanMode === 'SCRAP' || scanMode === 'INTERNAL' || scanMode === 'SALE';
      if (!isSubtractionMode) return true;

      // Use provided product data if available, otherwise fall back to state
      const supplier = productData?.supplier || safeSupplierData[ean];
      const inventory = productData?.inventory || safeInventoryData[ean];

      const isKnown = !!supplier;
      if (!isKnown) return true;

      const currentQty = inventory?.quantity ?? 0;
      if (currentQty < requestedQty) {
        setOutOfStockError({
          ean,
          name: supplier?.name || ean,
        });
        playAudio('error');
        if (currentQty === 0) {
          toast.error(`Nincs készleten: ${supplier?.name || ean}`);
        } else {
          toast.error(`Csak ${currentQty} db van készleten: ${supplier?.name || ean}`);
        }
        return false;
      }
      return true;
    },
    [scanMode, safeSupplierData, safeInventoryData, playAudio]
  );

  // Update inventory (unified logic for both COUNT and SALE modes)
  // OPTIMISTIC UI: Updates state immediately, syncs in background
  const updateInventory = useCallback(
    async (
      ean: string,
      delta: number,
      skipStockCheck: boolean = false,
      explicitProductData?: SupplierProduct
    ): Promise<boolean> => {
      const details = explicitProductData || safeSupplierData[ean];
      if (!details) {
        console.warn('updateInventory: No product details for EAN:', ean);
        playAudio('error');
        return false;
      }

      if (
        !skipStockCheck &&
        !checkStock(
          ean,
          Math.abs(delta),
          explicitProductData ? { supplier: explicitProductData } : undefined
        )
      ) {
        return false;
      }

      // STEP 1: Save current state for potential rollback
      const currentItem = safeInventoryData[ean];
      const oldQty = currentItem?.quantity ?? 0;
      const oldMinQuantity = currentItem?.min_quantity ?? 0;
      const oldOnShoppingList = currentItem?.on_shopping_list ?? false;
      const oldScannedAt = currentItem?.scannedAt || new Date();

      // STEP 2: Calculate expected new quantity (OPTIMISTIC)
      const isSubtractionMode =
        scanMode === 'SALE' || scanMode === 'SCRAP' || scanMode === 'INTERNAL';
      const expectedNewQty = isSubtractionMode
        ? Math.max(0, oldQty - Math.abs(delta))
        : oldQty + Math.abs(delta);

      console.log(
        `[Optimistic Update] EAN: ${ean}, Old Qty: ${oldQty}, Delta: ${delta}, New Qty: ${expectedNewQty}`
      );

      // STEP 3: Update state immediately (OPTIMISTIC UI)
      setInventoryData((prev) => ({
        ...prev,
        [ean]: {
          ean,
          quantity: expectedNewQty,
          scannedAt: new Date(),
          min_quantity: prev[ean]?.min_quantity ?? oldMinQuantity,
          on_shopping_list: prev[ean]?.on_shopping_list ?? oldOnShoppingList,
        },
      }));

      // STEP 4: Start network call in background
      try {
        const result = await supabaseService.updateItemQuantity(
          ean,
          delta,
          details,
          scanMode,
          false,
          oldQty
        );

        if (!result) {
          // Rollback: Network call failed
          console.log(`[Sync Failed - Rolling Back] EAN: ${ean}, Restoring Qty: ${oldQty}`);
          setInventoryData((prev) => ({
            ...prev,
            [ean]: {
              ean,
              quantity: oldQty,
              scannedAt: oldScannedAt,
              min_quantity: oldMinQuantity,
              on_shopping_list: oldOnShoppingList,
            },
          }));
          playAudio('error');
          toast.error(
            `Szinkronizációs hiba: ${details.name || ean}. Visszaállítva az eredeti értékre.`
          );
          return false;
        }

        // STEP 5: Sync success - update with server response (may differ from optimistic)
        const serverNewQty = result.new_quantity;
        console.log(`[Sync Success] EAN: ${ean}, Server Qty: ${serverNewQty}`);

        setInventoryData((prev) => ({
          ...prev,
          [ean]: {
            ean,
            quantity: serverNewQty,
            scannedAt: new Date(result.updated_at),
            min_quantity: result.min_quantity ?? prev[ean]?.min_quantity ?? oldMinQuantity,
            on_shopping_list:
              result.on_shopping_list ?? prev[ean]?.on_shopping_list ?? oldOnShoppingList,
          },
        }));

        // Explicit logging for RETURN, SCRAP, INTERNAL transactions (similar to checkout)
        if (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') {
          const productName = details.name || ean;
          const productUnit = details.unit || 'db';
          const logDetails = `${productName} - Mennyiség: ${delta} ${productUnit}, Új készlet: ${serverNewQty} ${productUnit}`;

          await supabaseService.logAction(
            scanMode,
            logDetails,
            false,
            supabaseService.addToQueue.bind(supabaseService)
          );
        }

        // Low stock warning
        const isSubtractionMode =
          scanMode === 'SALE' ||
          scanMode === 'SCRAP' ||
          scanMode === 'INTERNAL' ||
          scanMode === 'RETURN';
        if (
          isSubtractionMode &&
          result.low_stock &&
          result.min_quantity !== null &&
          result.min_quantity !== undefined
        ) {
          const productUnit = details.unit || 'db';
          toast(
            `Figyelem: A készlet a limit alá esett! (Jelenleg: ${serverNewQty} ${productUnit}) - Bekerült a kifogyóban listára.`,
            {
              duration: 6000,
              icon: '⚠️',
              style: {
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                color: '#991B1B',
              },
            }
          );
        }

        // Auto-remove from shopping list
        if ((result as any).removed_from_shopping_list && result.product_name) {
          toast.success(`Készlet feltöltve! ${result.product_name} lekerült a bevásárlólistáról.`, {
            duration: 5000,
            icon: '✅',
            style: {
              background: '#F0FDF4',
              border: '1px solid #86EFAC',
              color: '#166534',
            },
          });
        }

        playAudio('success');
        return true;
      } catch (error) {
        // Rollback: Network error occurred
        console.error(`[Sync Failed - Rolling Back] EAN: ${ean}, Error:`, error);
        setInventoryData((prev) => ({
          ...prev,
          [ean]: {
            ean,
            quantity: oldQty,
            scannedAt: oldScannedAt,
            min_quantity: oldMinQuantity,
            on_shopping_list: oldOnShoppingList,
          },
        }));
        playAudio('error');
        toast.error(`Hálózati hiba: ${details.name || ean}. Visszaállítva az eredeti értékre.`);
        return false;
      }
    },
    [safeSupplierData, safeInventoryData, scanMode, checkStock, setInventoryData, playAudio]
  );

  // Handle SALE mode with POS cart
  // REFACTORED: Accept productData + inventoryData directly to use fresh data, not stale state
  const handleSaleModeScan = useCallback(
    async (
      code: string,
      productData?: SupplierProduct,
      freshInventory?: { quantity: number }
    ): Promise<boolean> => {
      if (!hasPosModule) return false;

      // Check if it's a user card
      if (code.startsWith('USER-')) {
        updateSavedState({
          isCheckoutModalOpen: true,
          checkoutStep: 'SELECT_TYPE',
        });
        updateSavedState({ barcodeInput: '' });
        playAudio('success');
        returnFocusToMainInput();
        return true;
      }

      // CRITICAL: Use passed-in productData immediately, fallback to state only if not provided
      const product = productData || safeSupplierData[code];

      // Build stock check data with both supplier AND inventory from fresh DB fetch
      const stockCheckData = productData
        ? { supplier: productData, inventory: freshInventory }
        : undefined;

      // Check stock before adding to cart (for new items, check 1 qty)
      if (!checkStock(code, 1, stockCheckData)) {
        return false;
      }

      // Add to cart using the product data (from parameter or state)
      const productName = product?.name || 'Ismeretlen';
      const productPrice = product?.sellingPrice || 0;
      const productUnit = product?.unit || 'db';

      const existingItemIndex = safeCart.findIndex((item) => item.ean === code);

      if (existingItemIndex >= 0) {
        const existingQty = safeCart[existingItemIndex].quantity;
        const newTotalQty = existingQty + 1;

        // Check if we have enough stock for the new total quantity
        if (!checkStock(code, newTotalQty, stockCheckData)) {
          return false;
        }

        updateSavedState({
          cart: safeCart.map((item, idx) =>
            idx === existingItemIndex ? { ...item, quantity: newTotalQty } : item
          ),
        });
        toast.success(`${productName} hozzáadva (${newTotalQty} ${productUnit})`);
      } else {
        // For new items, we already checked stock for 1 item above
        updateSavedState({
          cart: [...safeCart, { ean: code, name: productName, price: productPrice, quantity: 1 }],
        });
        toast.success(`${productName} hozzáadva a kosárhoz`);
      }

      updateSavedState({ barcodeInput: '' });
      playAudio('success');
      returnFocusToMainInput();
      return true;
    },
    [hasPosModule, safeSupplierData, safeCart, updateSavedState, playAudio, returnFocusToMainInput]
  );

  // Main scan handler (unified triggerScan + updateInventory logic)
  // CRITICAL: Hardware reliability - sanitization and optimistic clearing happen FIRST
  const handleScan = useCallback(
    async (skipConfirmation: boolean = false): Promise<void> => {
      // STEP 1: Hardware Sanitization (Hungarian Keyboard Layout Fixes)
      const code = barcodeInput
        .replace(/ö/g, '0')
        .replace(/Ö/g, '0')
        .replace(/ü/g, '-')
        .replace(/Ü/g, '-')
        .replace(/y/g, 'z')
        .replace(/Y/g, 'Z')
        .trim();

      // STEP 1.5: User Card Detection (Checkout Trigger or Transaction Confirmation) - THE GATEKEEPER
      // CRITICAL: This MUST be the absolute first thing after sanitization
      // 1. Karakterjavítás (ü -> - fix)
      const sanitizedCode = barcodeInput.replace(/ü/g, '-').replace(/Ü/g, '-').trim().toUpperCase();

      // 2. AZONNALI ELKAPÁS (A sorompó)
      if (sanitizedCode.startsWith('USER')) {
        // Azonnal ürítjük az inputot és megállítjuk a termékkeresést
        updateSavedState({ barcodeInput: '' });

        // Eladó azonosítása az adatbázisból
        const userName = await supabaseService.getUserByBarcode(sanitizedCode);
        const identifiedName = userName || 'Ismeretlen felhasználó';

        // Handle USER card scan based on scanMode
        switch (scanMode) {
          case 'SALE':
            // SALE mode: Open PaymentModal for payment type selection
            if (hasPosModule && safeCart.length > 0) {
              setIdentifiedSellerName(identifiedName);
              updateSavedState({
                isCheckoutModalOpen: true,
                checkoutStep: 'SELECT_TYPE',
              });
            }
            break;

          case 'RETURN':
            // RETURN mode: Also open PaymentModal (refund requires payment method)
            if (hasPosModule && safeCart.length > 0) {
              setIdentifiedSellerName(identifiedName);
              updateSavedState({
                isCheckoutModalOpen: true,
                checkoutStep: 'SELECT_TYPE',
              });
            } else if (pendingAdjustment) {
              // Single item adjustment: set seller name for finalization
              setIdentifiedSellerName(identifiedName);
            }
            break;

          case 'SCRAP':
          case 'INTERNAL':
            // SCRAP/INTERNAL: Same order as SALE/RETURN — first SELECT_TYPE, then CONFIRM
            if (pendingAdjustment) {
              // Single item adjustment: set seller name and open confirmation
              setIdentifiedSellerName(identifiedName);
              // ConfirmationModal is already open via handleApprove
            } else if (pendingTransactions.length > 0) {
              // Multiple items: use existing transaction confirmation modal
              setTransactionUser(identifiedName);
              if (!showTransactionConfirmationModal) {
                setShowTransactionConfirmationModal(true);
              }
            } else if (safeCart.length > 0) {
              // Cart-based: open SELECT_TYPE first (then user goes to CONFIRM via UI)
              setIdentifiedSellerName(identifiedName);
              updateSavedState({
                isCheckoutModalOpen: true,
                checkoutStep: 'SELECT_TYPE',
              });
            }
            break;

          default:
            // COUNT mode or other: no action needed
            break;
        }

        isProcessingRef.current = false;
        returnFocusToMainInput();
        return; // <--- ÉLETMENTŐ: Megállítja a függvényt, nem lesz belőle termék!
      }

      // STEP 2: Optimistic Input Clearing - clear immediately for next scan
      updateSavedState({ barcodeInput: '' });

      // STEP 3: Race Condition Guard - prevent duplicate processing
      if (isProcessingRef.current) {
        return;
      }

      // Early return: empty code
      if (!code) {
        returnFocusToMainInput();
        return;
      }

      // Set processing flag
      isProcessingRef.current = true;

      try {
        // STEP 4: FETCH fresh data from database (The Gatekeeper)
        // CRITICAL: Always fetch fresh data to avoid stale state issues
        const freshData = await supabaseService.getProductByEan(code);

        // DECIDE: Use freshData.supplier directly for ALL checks
        const currentProduct = freshData?.supplier || safeSupplierData[code];
        const currentInventory = freshData
          ? {
              ean: code,
              quantity: freshData.inventory.quantity,
              scannedAt: freshData.inventory.scannedAt,
              min_quantity: safeInventoryData[code]?.min_quantity ?? 0,
              on_shopping_list: safeInventoryData[code]?.on_shopping_list ?? false,
            }
          : safeInventoryData[code];

        // STEP 4.5: INTERCEPTION (Gatekeeper) - THE VERY FIRST THING AFTER DATABASE FETCH
        // CRITICAL: This MUST run immediately after fetch, before any other logic

        // For RETURN/SCRAP/INTERNAL modes: ALWAYS stop and show ProductCard
        if (
          freshData?.supplier &&
          (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL')
        ) {
          // Update state for UI consistency
          setSupplierData((prev) => ({
            ...prev,
            [code]: freshData.supplier,
          }));
          setInventoryData((prev) => ({
            ...prev,
            [code]: {
              ean: code,
              quantity: freshData.inventory.quantity,
              scannedAt: freshData.inventory.scannedAt,
              min_quantity: prev[code]?.min_quantity ?? 0,
              on_shopping_list: prev[code]?.on_shopping_list ?? false,
            },
          }));

          // Show ProductCard immediately
          updateSavedState({ lastScanned: code, transactionQty: 1 });
          playAudio('success');
          isProcessingRef.current = false;
          returnFocusToMainInput();
          return; // CRITICAL: Stop here to show ProductCard! No exceptions.
        }

        // Gatekeeper for SALE mode: An item must STOP and show the card if:
        // - is_weighable is TRUE OR
        // - unit is 'm' OR
        // - unit is 'kg'
        if (freshData?.supplier && scanMode === 'SALE') {
          const isWeighable =
            freshData.supplier.isWeighable === true ||
            (freshData.supplier as any).is_weighable === true;
          const currentUnit = freshData.supplier.unit || 'db';

          // Local constant for interception check
          const shouldIntercept = isWeighable || currentUnit === 'm' || currentUnit === 'kg';

          if (shouldIntercept) {
            // Update state for UI consistency (before interception)
            setSupplierData((prev) => ({
              ...prev,
              [code]: freshData.supplier,
            }));
            setInventoryData((prev) => ({
              ...prev,
              [code]: {
                ean: code,
                quantity: freshData.inventory.quantity,
                scannedAt: freshData.inventory.scannedAt,
                min_quantity: prev[code]?.min_quantity ?? 0,
                on_shopping_list: prev[code]?.on_shopping_list ?? false,
              },
            }));

            const unitWeight =
              freshData.supplier.unitWeight ?? (freshData.supplier as any).unit_weight ?? 0;

            // If weighable with valid unitWeight, show weight modal
            if (isWeighable && unitWeight && unitWeight > 0 && !isNaN(unitWeight)) {
              // CRITICAL: Update states and return immediately to stop the leak
              setPendingWeighableItem({
                ean: code,
                product: freshData.supplier,
                inventory: currentInventory || {
                  ean: code,
                  quantity: 0,
                  scannedAt: new Date(),
                },
              });
              setShowWeightModal(true);
              updateSavedState({ lastScanned: code });
              playAudio('success');
              isProcessingRef.current = false; // Reset lock
              returnFocusToMainInput();
              return; // CRITICAL: Stop here to prevent leak! No exceptions.
            } else {
              // For 'm' or 'kg' units, or weighable without unitWeight: show ProductCard
              updateSavedState({ lastScanned: code });
              playAudio('success');
              isProcessingRef.current = false; // Reset lock
              returnFocusToMainInput();
              return; // CRITICAL: Stop here to show ProductCard for manual quantity input! No exceptions.
            }
          }
        }

        // Update state for UI consistency (async, but we use freshData directly)
        if (freshData) {
          setSupplierData((prev) => ({
            ...prev,
            [code]: freshData.supplier,
          }));
          setInventoryData((prev) => ({
            ...prev,
            [code]: {
              ean: code,
              quantity: prev[code] ? prev[code].quantity : freshData.inventory.quantity,
              scannedAt: freshData.inventory.scannedAt,
              min_quantity:
                (freshData.inventory as any).min_quantity ?? prev[code]?.min_quantity ?? 0,
              on_shopping_list:
                (freshData.inventory as any).on_shopping_list ??
                prev[code]?.on_shopping_list ??
                false,
            },
          }));
        }

        const isKnown = !!currentProduct;

        // STEP 4.6: SALE mode logic (POS cart) - after weight check
        // CRITICAL: Only call handleSaleModeScan if rapid mode is enabled
        // If rapid mode is OFF, show ProductCard for confirmation (same as COUNT mode)
        if (scanMode === 'SALE') {
          if (isRapidMode) {
            // Rapid mode: add to cart immediately (pass fresh inventory for accurate stock check)
            const handled = await handleSaleModeScan(code, freshData?.supplier, currentInventory);
            if (handled) {
              isProcessingRef.current = false;
              returnFocusToMainInput();
              return;
            }
          } else {
            // Normal mode: show ProductCard for confirmation (same as COUNT mode)
            // Don't call handleSaleModeScan here - it will be called in handleApprove
            // Just set lastScanned to show the card
            updateSavedState({ lastScanned: code, transactionQty: 1 });
            playAudio('success');
            isProcessingRef.current = false;
            returnFocusToMainInput();
            return;
          }
        }

        // STEP 5: Stock check for subtraction modes
        // Pass fresh data directly to avoid state lookup
        if (
          !checkStock(
            code,
            1,
            freshData ||
              (currentProduct && currentInventory
                ? { supplier: currentProduct, inventory: currentInventory }
                : undefined)
          )
        ) {
          isProcessingRef.current = false;
          returnFocusToMainInput();
          return;
        }

        // STEP 6: Handle consecutive scans
        if (lastScanned) {
          // Same barcode scanned again
          if (code === lastScanned) {
            setOutOfStockError(null);
            if (!isKnown) {
              returnFocusToMainInput();
              return;
            }

            // CRITICAL: Force fresh DB fetch for consecutive scans to prevent stale data
            const consecutiveFreshData = await supabaseService.getProductByEan(code);
            const consecutiveProduct = consecutiveFreshData?.supplier || currentProduct;
            const consecutiveInventory = consecutiveFreshData
              ? {
                  ean: code,
                  quantity: consecutiveFreshData.inventory.quantity,
                  scannedAt: consecutiveFreshData.inventory.scannedAt,
                  min_quantity: safeInventoryData[code]?.min_quantity ?? 0,
                  on_shopping_list: safeInventoryData[code]?.on_shopping_list ?? false,
                }
              : currentInventory;

            // Check stock again for subtraction modes
            // Use fresh product data
            const stockCheckData =
              consecutiveProduct && consecutiveInventory
                ? { supplier: consecutiveProduct, inventory: consecutiveInventory }
                : undefined;
            if (!checkStock(code, 1, stockCheckData)) {
              returnFocusToMainInput();
              return;
            }

            // Increment quantity
            const newQty = transactionQty + 1;
            updateSavedState({ transactionQty: newQty });
            // For RETURN/SCRAP/INTERNAL modes, don't update inventory immediately
            if (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') {
              // Don't update inventory, quantity is already updated in state
              // Will be processed when user card is scanned
            } else {
              // CRITICAL: Use fresh data from DB fetch, not stale state
              await updateInventory(code, 1, true, consecutiveFreshData?.supplier);
            }
            returnFocusToMainInput();
            return;
          }

          // Different barcode - auto-approve previous item
          setOutOfStockError(null);
          const previousCode = lastScanned;
          const previousIsKnown = !!safeSupplierData[previousCode];

          if (previousIsKnown) {
            // For RETURN/SCRAP/INTERNAL modes, add to pending transactions instead of updating inventory
            if (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') {
              const details = safeSupplierData[previousCode];
              const productName = details.name || 'Ismeretlen';
              const productUnit = details.unit || 'db';

              setPendingTransactions((prev) => {
                const existingIndex = prev.findIndex((t) => t.ean === previousCode);
                if (existingIndex >= 0) {
                  return prev.map((t, idx) =>
                    idx === existingIndex ? { ...t, quantity: t.quantity + transactionQty } : t
                  );
                } else {
                  return [
                    ...prev,
                    {
                      ean: previousCode,
                      name: productName,
                      quantity: transactionQty,
                      mode: scanMode,
                      unit: productUnit,
                    },
                  ];
                }
              });

              toast.success(
                `${productName} hozzáadva a tranzakcióhoz (${transactionQty} ${productUnit})`
              );
              toast('Olvassd be a user kártyát a véglegesítéshez!', {
                duration: 4000,
                icon: '👤',
              });
              playAudio('success');
            } else {
              try {
                const details = safeSupplierData[previousCode];
                const oldQty = safeInventoryData[previousCode]?.quantity ?? 0;
                const result = await supabaseService.updateItemQuantity(
                  previousCode,
                  transactionQty,
                  details,
                  scanMode,
                  false,
                  oldQty
                );

                if (result) {
                  const newQty = result.new_quantity;
                  setInventoryData((prev) => ({
                    ...prev,
                    [previousCode]: {
                      ean: previousCode,
                      quantity: newQty,
                      scannedAt: new Date(result.updated_at),
                      min_quantity: result.min_quantity ?? prev[previousCode]?.min_quantity ?? 0,
                      on_shopping_list:
                        result.on_shopping_list ?? prev[previousCode]?.on_shopping_list ?? false,
                    },
                  }));

                  if (
                    result.low_stock &&
                    result.min_quantity !== null &&
                    result.min_quantity !== undefined
                  ) {
                    const previousProductUnit = safeSupplierData[previousCode]?.unit || 'db';
                    toast(
                      (t) => (
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="font-bold text-red-600 dark:text-red-400">
                              ⚠️ Figyelem:{' '}
                              {result.product_name ||
                                safeSupplierData[previousCode]?.name ||
                                previousCode}{' '}
                              készlete kritikus!
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Jelenlegi készlet: {newQty} {previousProductUnit} (Limit:{' '}
                              {result.min_quantity} {previousProductUnit})
                            </p>
                          </div>
                          {!result.on_shopping_list && (
                            <button
                              onClick={async () => {
                                try {
                                  await supabaseService.toggleShoppingList(previousCode, true);
                                  setInventoryData((prev) => ({
                                    ...prev,
                                    [previousCode]: {
                                      ...prev[previousCode],
                                      on_shopping_list: true,
                                    },
                                  }));
                                  toast.success('Hozzáadva a bevásárlólistához!', { id: t.id });
                                } catch (error) {
                                  console.error('Failed to add to shopping list:', error);
                                  toast.error('Hiba a bevásárlólistához adásnál', { id: t.id });
                                }
                              }}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm transition shadow-md"
                            >
                              Listára teszem
                            </button>
                          )}
                        </div>
                      ),
                      {
                        duration: 10000,
                        icon: '⚠️',
                        style: {
                          background: '#FEF2F2',
                          border: '1px solid #FCA5A5',
                          color: '#991B1B',
                        },
                      }
                    );
                  }

                  playAudio('success');
                }
              } catch (error) {
                console.error('Auto-approve previous item error:', error);
                playAudio('error');
              }
            }
          }

          // STEP 7: Reset form and clear errors for new scan
          updateSavedState({
            newName: '',
            newPrice: '',
            newSellingPrice: '',
            newPurchaseNet: undefined,
            newPurchaseGross: undefined,
            newSellingNet: undefined,
            newSellingGross: undefined,
            newVatRate: 27,
            newMinQuantity: '',
            newIsWeighable: false,
            newUnitWeight: '',
            newUnit: 'db',
            transactionQty: 1,
          });
          setOutOfStockError(null);

          // STEP 8: Process new scan (isKnown already set in STEP 4)
          if (isKnown && currentProduct) {
            if (skipConfirmation) {
              // Rapid mode: For RETURN/SCRAP/INTERNAL modes, show ProductCard for quantity confirmation
              if (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') {
                updateSavedState({ lastScanned: code, transactionQty: 1 });
                playAudio('success');
                returnFocusToMainInput();
                return; // Show ProductCard, don't add to pending transactions yet
              } else {
                // Rapid mode: process immediately for other modes
                const details = currentProduct;
                const oldQty = currentInventory?.quantity ?? 0;
                const result = await supabaseService.updateItemQuantity(
                  code,
                  1,
                  details,
                  scanMode,
                  false,
                  oldQty
                );

                if (result) {
                  const newQty = result.new_quantity;
                  setInventoryData((prev) => ({
                    ...prev,
                    [code]: {
                      ean: code,
                      quantity: newQty,
                      scannedAt: new Date(result.updated_at),
                    },
                  }));

                  const deltaText =
                    (scanMode as ScanMode) === 'SALE' ||
                    (scanMode as ScanMode) === 'SCRAP' ||
                    (scanMode as ScanMode) === 'INTERNAL'
                      ? '-1'
                      : (scanMode as ScanMode) === 'RETURN'
                        ? '+1'
                        : '+1';
                  const productUnit = details.unit || 'db';
                  toast.success(`${deltaText} ${details.name || code} (${newQty} ${productUnit})`);
                  playAudio('success');
                } else {
                  playAudio('error');
                  toast.error(`Hiba: ${code}`);
                }
              }
            } else {
              // Normal mode: show confirmation card
              updateSavedState({ lastScanned: code, transactionQty: 1 });
              // For RETURN/SCRAP/INTERNAL modes, don't update inventory immediately
              // Wait for user card confirmation
              if (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') {
                // Don't update inventory, just show the card
                // Inventory will be updated after user card confirmation
              } else {
                playAudio('success');
              }
            }
          } else {
            // Unknown product: show add product modal immediately
            if (!skipConfirmation) {
              updateSavedState({ lastScanned: code, transactionQty: 1 });

              // Auto-focus name input for scanner input (code length > 5 indicates scanner)
              // The useEffect hook will handle focus, but we can also do it immediately for better UX
              if (code.length > 5) {
                // Scanner input: immediately focus name input
                // Clear any pending name input timeout to prevent memory leak
                if (nameInputTimeoutRef.current) {
                  clearTimeout(nameInputTimeoutRef.current);
                }
                nameInputTimeoutRef.current = setTimeout(() => {
                  nameInputRef.current?.focus();
                  nameInputRef.current?.select();
                  nameInputTimeoutRef.current = null;
                }, 150);
              }
              // For manual typing (code.length <= 5), user will press Enter again, which will trigger handleScan
              // and the useEffect will handle focus
            } else {
              toast.error(`Ismeretlen termék: ${code}`);
              playAudio('error');
              returnFocusToMainInput();
            }
          }

          // Don't return focus to main input if unknown product with scanner input
          // The useEffect hook will handle focus appropriately
          if (isKnown || (code.length <= 5 && !skipConfirmation)) {
            returnFocusToMainInput();
          }
        } else {
          // STEP 6.5: First scan (no lastScanned) - handle known/unknown products
          if (isKnown && currentProduct) {
            // Known product: show ProductCard or process immediately
            if (skipConfirmation) {
              // Rapid mode: process immediately
              const details = currentProduct;
              const oldQty = currentInventory?.quantity ?? 0;
              const result = await supabaseService.updateItemQuantity(
                code,
                1,
                details,
                scanMode,
                false,
                oldQty
              );

              if (result) {
                const newQty = result.new_quantity;
                setInventoryData((prev) => ({
                  ...prev,
                  [code]: {
                    ean: code,
                    quantity: newQty,
                    scannedAt: new Date(result.updated_at),
                  },
                }));

                const deltaText =
                  scanMode === 'SALE' || scanMode === 'SCRAP' || scanMode === 'INTERNAL'
                    ? '-1'
                    : scanMode === 'RETURN'
                      ? '+1'
                      : '+1';
                const productUnit = details.unit || 'db';
                toast.success(`${deltaText} ${details.name || code} (${newQty} ${productUnit})`);
                playAudio('success');
              } else {
                playAudio('error');
                toast.error(`Hiba: ${code}`);
              }
            } else {
              // Normal mode: show confirmation card
              updateSavedState({ lastScanned: code, transactionQty: 1 });
              // For RETURN/SCRAP/INTERNAL modes, don't update inventory immediately
              if (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') {
                // Don't update inventory, just show the card
              } else {
                playAudio('success');
              }
            }
          } else {
            // Unknown product: show add product modal immediately
            console.log('Ismeretlen termék, felvétel indítása:', code);
            updateSavedState({ lastScanned: code, transactionQty: 1 });
            playAudio('error');

            // Auto-focus name input for scanner input (code length > 5 indicates scanner)
            // The useEffect hook will handle focus, but we can also do it immediately for better UX
            if (code.length > 5) {
              // Scanner input: immediately focus name input
              // Clear any pending name input timeout to prevent memory leak
              if (nameInputTimeoutRef.current) {
                clearTimeout(nameInputTimeoutRef.current);
              }
              nameInputTimeoutRef.current = setTimeout(() => {
                nameInputRef.current?.focus();
                nameInputRef.current?.select();
                nameInputTimeoutRef.current = null;
              }, 150);
            }
            // For manual typing (code.length <= 5), user will press Enter again, which will trigger handleScan
            // and the useEffect will handle focus
          }

          isProcessingRef.current = false;
          // Don't return focus to main input if unknown product with scanner input
          // The useEffect hook will handle focus appropriately
          if (isKnown || code.length <= 5) {
            returnFocusToMainInput();
          }
        }
      } catch (error) {
        console.error('handleScan error:', error);
        playAudio('error');
        toast.error('Hiba a beszkennelés során');
      } finally {
        // Always clear processing flag
        isProcessingRef.current = false;
      }
    },
    [
      barcodeInput,
      scanMode,
      lastScanned,
      transactionQty,
      scanMode,
      safeSupplierData,
      safeInventoryData,
      handleSaleModeScan,
      checkStock,
      updateInventory,
      setInventoryData,
      updateSavedState,
      playAudio,
      returnFocusToMainInput,
      hasPosModule,
      pendingTransactions,
      showTransactionConfirmationModal,
    ]
  );

  // Quantity change handler - ONLY updates local state, NO database calls
  const handleQtyChange = useCallback(
    (newValStr: string) => {
      // Support decimal numbers for 'm' and 'kg' units (case-insensitive: KG/kg, M/m)
      const currentUnit = lastScanned ? safeSupplierData[lastScanned]?.unit || 'db' : newUnit;
      const unitLower = (currentUnit ?? '').toString().toLowerCase();
      const isDecimalUnit = unitLower === 'kg' || unitLower === 'm';

      // Fix decimal input: replace comma with dot
      const sanitizedVal = newValStr.replace(',', '.');

      // Allow empty input: user is clearing the field to type a new number.
      // Don't snap back to 0 — the ProductCard shows placeholder "0" visually.
      if (sanitizedVal === '') {
        updateSavedState({ transactionQty: 0 });
        return;
      }

      // Don't update parent while user is typing a decimal (e.g. "0." → "0.2"); avoids overwriting display
      if (isDecimalUnit && sanitizedVal.endsWith('.')) {
        return;
      }

      // Parse value: use parseFloat for decimal units, parseInt for integer units
      let newVal: number;
      if (isDecimalUnit) {
        const parsed = parseFloat(sanitizedVal) || 0;
        // Round to 2 decimal places for 'm' and 'kg'
        newVal = Number(parsed.toFixed(2));
      } else {
        newVal = parseInt(sanitizedVal) || 0;
      }

      // CRITICAL: Only update local state, do NOT call updateInventory
      // Database update will happen in handleApprove when user confirms
      updateSavedState({ transactionQty: newVal });
    },
    [lastScanned, newUnit, safeSupplierData, updateSavedState]
  );

  // Handle weight confirmation from modal
  const handleWeightConfirm = useCallback(
    async (calculatedQty: number) => {
      if (!pendingWeighableItem) {
        return;
      }

      const { ean, product, inventory } = pendingWeighableItem;

      try {
        // Add to cart for SALE, RETURN, SCRAP, INTERNAL modes (unified cart logic)
        if (
          (scanMode === 'SALE' ||
            scanMode === 'RETURN' ||
            scanMode === 'SCRAP' ||
            scanMode === 'INTERNAL') &&
          hasPosModule
        ) {
          const productName = product.name || 'Ismeretlen';
          const productPrice = product.sellingPrice || 0;

          const existingItemIndex = safeCart.findIndex((item) => item.ean === ean);

          const productUnit = product.unit || 'db';
          if (existingItemIndex >= 0) {
            const existingQty = safeCart[existingItemIndex].quantity;
            const newTotalQty = existingQty + calculatedQty;

            // Check stock for SALE/SCRAP/INTERNAL modes (RETURN doesn't need check)
            if (
              (scanMode === 'SALE' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') &&
              !checkStock(ean, newTotalQty, { supplier: product, inventory })
            ) {
              return;
            }

            updateSavedState({
              cart: safeCart.map((item, idx) =>
                idx === existingItemIndex ? { ...item, quantity: newTotalQty } : item
              ),
            });
            toast.success(`${productName} hozzáadva (${newTotalQty} ${productUnit})`);
          } else {
            // Check stock for new weighable item in SALE/SCRAP/INTERNAL modes
            if (
              (scanMode === 'SALE' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') &&
              !checkStock(ean, calculatedQty, { supplier: product, inventory })
            ) {
              return;
            }

            updateSavedState({
              cart: [
                ...safeCart,
                { ean, name: productName, price: productPrice, quantity: calculatedQty },
              ],
            });
            toast.success(`${productName} hozzáadva a kosárhoz (${calculatedQty} ${productUnit})`);
          }

          // CRITICAL: Do NOT call updateItemQuantity here - checkout will handle inventory deduction
          playAudio('success');
        } else {
          // COUNT mode: Update inventory immediately
          const oldQty = inventory.quantity ?? 0;
          const result = await supabaseService.updateItemQuantity(
            ean,
            calculatedQty,
            product,
            scanMode,
            false,
            oldQty
          );

          if (result) {
            setInventoryData((prev) => ({
              ...prev,
              [ean]: {
                ean,
                quantity: result.new_quantity,
                scannedAt: new Date(result.updated_at),
                min_quantity: result.min_quantity ?? prev[ean]?.min_quantity ?? 0,
                on_shopping_list: result.on_shopping_list ?? prev[ean]?.on_shopping_list ?? false,
              },
            }));

            const deltaText = '+1';
            const productUnit = product.unit || 'db';
            toast.success(
              `${deltaText} ${product.name || ean} (${result.new_quantity} ${productUnit})`
            );
            playAudio('success');
          } else {
            playAudio('error');
          }
        }
      } catch (error) {
        console.error('handleWeightConfirm error:', error);
        playAudio('error');
        toast.error('Hiba a súlyalapú mennyiség rögzítésénél');
      } finally {
        // Close modal and clear pending item
        setShowWeightModal(false);
        setPendingWeighableItem(null);
        returnFocusToMainInput();
      }
    },
    [
      pendingWeighableItem,
      scanMode,
      hasPosModule,
      safeCart,
      updateSavedState,
      setInventoryData,
      playAudio,
      returnFocusToMainInput,
    ]
  );

  // Save new product
  const handleSaveNewProduct = useCallback(async () => {
    if (!lastScanned || !newName) {
      toast.error('Kérlek add meg a termék nevét!');
      playAudio('error');
      return;
    }

    // Validate weight fields if weighable
    const isWeighable = savedState.newIsWeighable ?? false;
    const unitWeightValue = parseFloat(savedState.newUnitWeight || '0') || 0;

    if (isWeighable) {
      if (unitWeightValue <= 0 || isNaN(unitWeightValue)) {
        toast.error('Kérlek add meg az egységsúlyt (nagyobb, mint 0)!');
        playAudio('error');
        return;
      }
    }

    // CRITICAL FIX: Read values directly from savedState, not from closure
    // This ensures we get the latest values that the user just entered in the ProductCard
    const currentPurchaseNet = savedState.newPurchaseNet;
    const currentSellingNet = savedState.newSellingNet;
    const currentSellingGross = savedState.newSellingGross;
    const currentVatRate = savedState.newVatRate ?? 27;
    const currentMinQuantity = savedState.newMinQuantity ?? '';
    const currentUnit = savedState.newUnit ?? 'db';

    // PRIORITIZE EXPLICIT NET VALUE: If newPurchaseNet is defined (even if 0), use it
    // Only fallback to legacy newPrice if newPurchaseNet is undefined/null
    let purchaseNet: number;
    if (currentPurchaseNet !== undefined && currentPurchaseNet !== null) {
      // Explicit net value was provided - use it as source of truth
      purchaseNet = currentPurchaseNet;
    } else {
      // Fallback to legacy newPrice field (backward compatibility)
      purchaseNet = newPrice ? parseFloat(newPrice.replace(/[^0-9.]/g, '')) || 0 : 0;
    }

    // Same logic for selling net price
    let sellingNet: number;
    if (currentSellingNet !== undefined && currentSellingNet !== null) {
      // Explicit net selling value was provided - use it as source of truth
      sellingNet = currentSellingNet;
    } else {
      // Fallback to legacy newSellingPrice field (backward compatibility)
      sellingNet = newSellingPrice ? parseFloat(newSellingPrice.replace(/[^0-9.]/g, '')) || 0 : 0;
    }

    const vatRate = currentVatRate;

    // Calculate Gross from Net if not provided (uploadSupplierData will recalculate, but we keep for consistency)
    const sellingGross =
      currentSellingGross ??
      (sellingNet > 0 ? Math.round(sellingNet * (1 + vatRate / 100) * 100) / 100 : 0);

    // Jelzési limit: kg/m esetén tizedes, db esetén egész
    // NULL = no limit (empty field), 0 = valid limit
    const isKgOrM = currentUnit === 'kg' || currentUnit === 'm';
    const rawMinQtyStr = String(currentMinQuantity).trim();
    let minQty: number | null;
    if (rawMinQtyStr === '') {
      minQty = null; // Empty field = no limit
    } else {
      const parsed = isKgOrM
        ? parseFloat(rawMinQtyStr.replace(',', '.'))
        : parseInt(rawMinQtyStr.replace(/[^0-9]/g, ''), 10);
      minQty = isNaN(parsed) || parsed < 0 ? null : parsed;
    }
    const initialQty = transactionQty > 0 ? transactionQty : 1;

    // DEBUG LOGGING: Show exactly what values are being used
    console.log(
      'Saving Product - Net:',
      savedState.newPurchaseNet,
      'Gross:',
      savedState.newPurchaseGross,
      'VAT:',
      savedState.newVatRate
    );
    console.log(
      'Calculated purchaseNet:',
      purchaseNet,
      'sellingNet:',
      sellingNet,
      'sellingGross:',
      sellingGross
    );

    // Prepare the data object with hybrid pricing
    const newProduct: SupplierProduct = {
      ean: lastScanned,
      name: newName,
      netPrice: purchaseNet, // Net purchase price - MUST be the value user typed
      sellingPrice: sellingGross, // Gross selling price (for POS compatibility)
      netSellingPrice: sellingNet, // Net selling price (for profit calculations)
      vatRate: vatRate,
      sku: '',
      // Explicitly map the state variables:
      isWeighable: isWeighable,
      unitWeight: unitWeightValue,
      unit: currentUnit || 'db',
      // CRITICAL FIX: Include minQuantity in creation payload so it's set atomically
      // This prevents the race condition where Realtime events overwrite min_quantity=0
      minQuantity: minQty,
    };

    try {
      await supabaseService.uploadSupplierData([newProduct], 'COUNT');

      const result = await supabaseService.updateItemQuantity(
        lastScanned,
        initialQty,
        newProduct,
        'COUNT',
        false,
        0
      );

      if (result) {
        try {
          await supabaseService.updateMinQuantity(lastScanned, minQty);
        } catch (minQtyError) {
          console.warn('Failed to update min_quantity:', minQtyError);
        }

        // CRITICAL: Immediately update local states with full product object (including weight flags)
        // This prevents the need for a page refresh
        setInventoryData((prev) => ({
          ...prev,
          [lastScanned]: {
            ean: lastScanned,
            quantity: result.new_quantity,
            scannedAt: new Date(result.updated_at),
            min_quantity: minQty,
            on_shopping_list: false,
            unit: newUnit || 'db',
          },
        }));

        // CRITICAL: Update supplierData with complete product object
        // newProduct already contains isWeighable, unitWeight, and unit
        setSupplierData((prev) => ({
          ...prev,
          [lastScanned]: newProduct,
        }));

        playAudio('success');
      } else {
        playAudio('error');
      }

      updateSavedState({
        lastScanned: null,
        barcodeInput: '',
        newName: '',
        newPrice: '',
        newSellingPrice: '',
        newPurchaseNet: undefined,
        newPurchaseGross: undefined,
        newSellingNet: undefined,
        newSellingGross: undefined,
        newVatRate: 27,
        newMinQuantity: '',
        newIsWeighable: false,
        newUnitWeight: '',
        newUnit: 'db',
      });
      returnFocusToMainInput();
    } catch (e) {
      console.error(e);
      const friendlyError = getUserFriendlyError(e);
      toast.error(friendlyError);
      playAudio('error');
    }
  }, [
    lastScanned,
    newName,
    savedState,
    newPrice,
    newSellingPrice,
    newMinQuantity,
    transactionQty,
    setInventoryData,
    setSupplierData,
    updateSavedState,
    playAudio,
    returnFocusToMainInput,
  ]);

  // Approve handler - THE FINAL GATE: Database update happens here
  const handleApprove = useCallback(async () => {
    if (!lastScanned) {
      setOutOfStockError(null);
      updateSavedState({ lastScanned: null });
      returnFocusToMainInput();
      return;
    }

    const productDetails = safeSupplierData[lastScanned];
    const isKnown = !!productDetails;
    const currentUnit = productDetails?.unit || 'db';

    // Handle SALE mode: Add to cart with transactionQty
    if (scanMode === 'SALE' && hasPosModule && isKnown && productDetails) {
      const productName = productDetails.name || 'Ismeretlen';
      const productPrice = productDetails.sellingPrice || 0;

      const existingItemIndex = safeCart.findIndex((item) => item.ean === lastScanned);

      if (existingItemIndex >= 0) {
        const existingQty = safeCart[existingItemIndex].quantity;
        const newTotalQty = existingQty + transactionQty;

        // Check stock for the new total quantity
        if (!checkStock(lastScanned, newTotalQty)) {
          return;
        }

        updateSavedState({
          cart: safeCart.map((item, idx) =>
            idx === existingItemIndex ? { ...item, quantity: newTotalQty } : item
          ),
          lastScanned: null,
        });
        toast.success(`${productName} hozzáadva (${newTotalQty} ${currentUnit})`);
      } else {
        // Check stock for new item
        if (!checkStock(lastScanned, transactionQty)) {
          return;
        }

        updateSavedState({
          cart: [
            ...safeCart,
            { ean: lastScanned, name: productName, price: productPrice, quantity: transactionQty },
          ],
          lastScanned: null,
        });
        toast.success(`${productName} hozzáadva a kosárhoz (${transactionQty} ${currentUnit})`);
      }

      playAudio('success');
      setOutOfStockError(null);
      returnFocusToMainInput();
      return;
    }

    // Handle RETURN/SCRAP/INTERNAL modes: Add to cart (same as SALE mode)
    if (
      (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') &&
      hasPosModule &&
      isKnown &&
      productDetails
    ) {
      const productName = productDetails.name || 'Ismeretlen';
      const productPrice = productDetails.sellingPrice || 0;

      const existingItemIndex = safeCart.findIndex((item) => item.ean === lastScanned);

      if (existingItemIndex >= 0) {
        const existingQty = safeCart[existingItemIndex].quantity;
        const newTotalQty = existingQty + transactionQty;

        // Check stock for SCRAP/INTERNAL modes (RETURN mode doesn't need stock check)
        if (
          (scanMode === 'SCRAP' || scanMode === 'INTERNAL') &&
          !checkStock(lastScanned, newTotalQty)
        ) {
          return;
        }

        updateSavedState({
          cart: safeCart.map((item, idx) =>
            idx === existingItemIndex ? { ...item, quantity: newTotalQty } : item
          ),
          lastScanned: null,
        });
        toast.success(`${productName} hozzáadva (${newTotalQty} ${currentUnit})`);
      } else {
        // Check stock for new item in SCRAP/INTERNAL modes
        if (
          (scanMode === 'SCRAP' || scanMode === 'INTERNAL') &&
          !checkStock(lastScanned, transactionQty)
        ) {
          return;
        }

        updateSavedState({
          cart: [
            ...safeCart,
            { ean: lastScanned, name: productName, price: productPrice, quantity: transactionQty },
          ],
          lastScanned: null,
        });
        toast.success(`${productName} hozzáadva a kosárhoz (${transactionQty} ${currentUnit})`);
      }

      playAudio('success');
      setOutOfStockError(null);
      returnFocusToMainInput();
      return;
    }

    // Check if this is a special unit item (is_weighable OR unit is 'm' OR unit is 'kg')
    const isSpecialUnit =
      isKnown &&
      (productDetails.isWeighable === true ||
        (productDetails as any).is_weighable === true ||
        productDetails.unit === 'm' ||
        productDetails.unit === 'kg');

    // If RETURN/SCRAP/INTERNAL mode and special unit, add to cart (unified cart logic)
    if (
      (scanMode === 'RETURN' || scanMode === 'SCRAP' || scanMode === 'INTERNAL') &&
      hasPosModule &&
      isSpecialUnit &&
      productDetails
    ) {
      const productName = productDetails.name || 'Ismeretlen';
      const productPrice = productDetails.sellingPrice || 0;

      const existingItemIndex = safeCart.findIndex((item) => item.ean === lastScanned);

      if (existingItemIndex >= 0) {
        updateSavedState({
          cart: safeCart.map((item, idx) =>
            idx === existingItemIndex ? { ...item, quantity: item.quantity + transactionQty } : item
          ),
          lastScanned: null,
        });
        toast.success(
          `${productName} hozzáadva (${safeCart[existingItemIndex].quantity + transactionQty} ${currentUnit})`
        );
      } else {
        updateSavedState({
          cart: [
            ...safeCart,
            { ean: lastScanned, name: productName, price: productPrice, quantity: transactionQty },
          ],
          lastScanned: null,
        });
        toast.success(`${productName} hozzáadva a kosárhoz (${transactionQty} ${currentUnit})`);
      }

      playAudio('success');
      setOutOfStockError(null);
      returnFocusToMainInput();
      return;
    } else if (isKnown && productDetails) {
      // COUNT mode: Use absolute quantity to force sync and prevent stale data issues
      if (scanMode === 'COUNT') {
        // CRITICAL: Calculate absolute target quantity from what user sees (not stale DB value)
        const currentInventory = safeInventoryData[lastScanned];
        const currentQty = currentInventory?.quantity ?? 0;
        const targetQty = currentQty + transactionQty;

        try {
          // Force update database with absolute value using editItem (overwrites stale data)
          // CRITICAL: Pass netPrice explicitly to prevent price shrinking bug
          await supabaseService.editItem(lastScanned, {
            name: productDetails.name || 'Ismeretlen',
            price: 0, // Not used when netPrice is provided
            quantity: targetQty, // Absolute target quantity
            unit: productDetails.unit || 'db',
            selling_price: productDetails.sellingPrice,
            isWeighable: productDetails.isWeighable,
            unitWeight: productDetails.unitWeight,
            minQuantity: currentInventory?.min_quantity,
            netPrice: productDetails.netPrice || 0, // CRITICAL: Explicit Net Price (source of truth)
            netSellingPrice: productDetails.netSellingPrice,
            vatRate: productDetails.vatRate || 27,
          });

          // Update local state with the absolute value
          setInventoryData((prev) => ({
            ...prev,
            [lastScanned]: {
              ean: lastScanned,
              quantity: targetQty,
              scannedAt: new Date(),
              min_quantity: prev[lastScanned]?.min_quantity ?? 0,
              on_shopping_list: prev[lastScanned]?.on_shopping_list ?? false,
            },
          }));

          // Manually log the COUNT action (editItem doesn't create transaction log)
          const productName = productDetails.name || lastScanned;
          const productUnit = productDetails.unit || 'db';
          const logDetails = `${productName} - Leltározva: ${targetQty} ${productUnit}`;
          await supabaseService.logAction(
            'COUNT',
            logDetails,
            false,
            supabaseService.addToQueue.bind(supabaseService)
          );

          toast.success(`${productName} leltározva: ${targetQty} ${productUnit}`);
          playAudio('success');
        } catch (error: any) {
          console.error('[handleApprove] COUNT mode editItem failed:', error);
          const errorMessage = error?.message || getUserFriendlyError(error);
          toast.error(`Hiba a leltározásnál: ${errorMessage}`);
          playAudio('error');
        }
      } else {
        // Other modes (SALE/RETURN/SCRAP/INTERNAL): Use delta-based updateInventory
        // CRITICAL: Force fresh DB fetch before updateInventory to prevent stale data
        const approveFreshData = await supabaseService.getProductByEan(lastScanned);
        const finalQty = transactionQty;
        await updateInventory(lastScanned, finalQty, false, approveFreshData?.supplier);
      }
    }

    setOutOfStockError(null);
    updateSavedState({ lastScanned: null });
    returnFocusToMainInput();
  }, [
    lastScanned,
    scanMode,
    hasPosModule,
    safeSupplierData,
    safeInventoryData,
    safeCart,
    transactionQty,
    updateSavedState,
    updateInventory,
    setInventoryData,
    playAudio,
    returnFocusToMainInput,
    setPendingTransactions,
    setShowTransactionConfirmationModal,
    setOutOfStockError,
  ]);

  // Discard handler
  const handleDiscard = useCallback(async () => {
    if (!lastScanned) return;

    updateSavedState({ lastScanned: null });
    returnFocusToMainInput();
  }, [
    lastScanned,
    scanMode,
    transactionQty,
    safeSupplierData,
    safeInventoryData,
    setInventoryData,
    updateSavedState,
    playAudio,
    returnFocusToMainInput,
  ]);

  // Generate internal EAN
  const handleGenerateInternalEan = useCallback(() => {
    const newEan = generateInternalEan();
    updateSavedState({
      lastScanned: newEan,
      barcodeInput: newEan,
      newName: '',
      newPrice: '',
      newSellingPrice: '',
      newPurchaseNet: undefined,
      newPurchaseGross: undefined,
      newSellingNet: undefined,
      newSellingGross: undefined,
      newVatRate: 27,
      newMinQuantity: '',
      newIsWeighable: false,
      newUnitWeight: '',
      newUnit: 'db',
    });
    playAudio('success');
    // Clear any pending generate EAN timeout to prevent memory leak
    if (generateEanTimeoutRef.current) {
      clearTimeout(generateEanTimeoutRef.current);
    }
    generateEanTimeoutRef.current = setTimeout(() => {
      nameInputRef.current?.focus();
      generateEanTimeoutRef.current = null;
    }, 200);
  }, [updateSavedState, playAudio]);

  // Name input with autocomplete
  const handleNameChange = useCallback(
    (val: string) => {
      updateSavedState({ newName: val });

      if (val.length > 2) {
        const matches = Object.values(safeSupplierData || {})
          .filter((p) => p?.name)
          .map((p) => p.name)
          .filter((name) => name?.toLowerCase().includes(val.toLowerCase()))
          .slice(0, 5);
        setNameSuggestions(matches);
        setShowSuggestions(matches.length > 0);
      } else {
        setNameSuggestions([]);
        setShowSuggestions(false);
      }
    },
    [safeSupplierData, updateSavedState]
  );

  // Handle inventory checkout for SCRAP/INTERNAL modes (no payment)
  const handleInventoryCheckout = useCallback(async () => {
    if (isProcessingCheckout || safeCart.length === 0) return;
    if (scanMode !== 'SCRAP' && scanMode !== 'INTERNAL') return;

    onCheckoutStart?.();
    setIsProcessingCheckout(true);

    try {
      // Prepare cart items with product details
      const cartItemsWithDetails = safeCart.map((item) => {
        const productDetails = safeSupplierData[item.ean];
        return {
          ean: item.ean,
          name: item.name,
          quantity: Number(item.quantity),
          price: productDetails?.netPrice || item.price,
          selling_price: productDetails?.sellingPrice || item.price,
          unit: productDetails?.unit || 'db',
          unit_weight: productDetails?.unitWeight,
          is_weighable: productDetails?.isWeighable,
        };
      });

      const baseNotes = `${scanMode === 'SCRAP' ? 'Selejtezés' : 'Saját felhasználás'} tranzakció - ${safeCart.length} termék`;
      const trimmedNote = transactionNote.trim();
      const finalNotes = trimmedNote ? `${baseNotes} | Note: ${trimmedNote}` : baseNotes;

      const result = await supabaseService.processInventoryBatch(
        scanMode,
        cartItemsWithDetails,
        identifiedSellerName || undefined,
        finalNotes
      );

      // Update inventory state
      setInventoryData((prev) => {
        const updated = { ...prev };
        (result?.updated_items || []).forEach((item) => {
          if (!item?.ean) return;
          const { ean, new_quantity } = item;
          const existingItem = prev[ean];
          updated[ean] = {
            ean,
            quantity: new_quantity ?? 0,
            scannedAt: new Date(),
            min_quantity: existingItem?.min_quantity ?? 0,
            on_shopping_list: existingItem?.on_shopping_list ?? false,
          };
        });
        return updated;
      });

      // Log the transaction
      const totalAmount = safeCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const totalQuantity = safeCart.reduce((sum, item) => sum + item.quantity, 0);
      const productNames = safeCart.map((item) => item.name || 'Ismeretlen').join(', ');
      const sellerText = identifiedSellerName ? ` | Felhasználó: ${identifiedSellerName}` : '';
      const modeText = scanMode === 'SCRAP' ? 'Selejtezés' : 'Saját felhasználás';
      const noteText = trimmedNote ? ` | Note: ${trimmedNote}` : '';
      const logDetails = `${modeText} tranzakció - ${productNames}, -${totalQuantity} db, Érték: ${totalAmount} Ft${sellerText}${noteText}`;

      await supabaseService.logAction(
        scanMode,
        logDetails,
        false,
        supabaseService.addToQueue.bind(supabaseService)
      );

      const successMessage =
        scanMode === 'SCRAP'
          ? `Selejtezés sikeresen véglegesítve! ${safeCart.length} termék feldolgozva.`
          : `Saját felhasználás sikeresen véglegesítve! ${safeCart.length} termék feldolgozva.`;
      toast.success(successMessage);
      playAudio('success');

      updateSavedState({
        cart: [],
        isCheckoutModalOpen: false,
        checkoutStep: 'SELECT_TYPE',
        discountPercent: 0,
      });
      setTransactionNote('');

      setIdentifiedSellerName('');
      returnFocusToMainInput();
    } catch (error: any) {
      console.error('[Inventory Checkout] ❌ Failed:', error);
      const errorMessage = error?.message || getUserFriendlyError(error);
      const modeText = scanMode === 'SCRAP' ? 'Selejtezés' : 'Saját felhasználás';
      toast.error(`${modeText} hiba: ${errorMessage}`);
      playAudio('error');
    } finally {
      setIsProcessingCheckout(false);
      onCheckoutComplete?.();
    }
  }, [
    isProcessingCheckout,
    safeCart,
    scanMode,
    safeSupplierData,
    setInventoryData,
    updateSavedState,
    playAudio,
    returnFocusToMainInput,
    onCheckoutStart,
    onCheckoutComplete,
    identifiedSellerName,
    transactionNote,
  ]);

  // Checkout handler (atomic batch processing)
  const handleCheckout = useCallback(async () => {
    if (isProcessingCheckout || safeCart.length === 0) return;

    // Route to appropriate handler based on scanMode
    if (scanMode === 'SCRAP' || scanMode === 'INTERNAL') {
      // SCRAP/INTERNAL: Use inventory transaction (no payment)
      await handleInventoryCheckout();
      return;
    }

    // SALE/RETURN: Use checkout transaction (with payment)
    onCheckoutStart?.();
    setIsProcessingCheckout(true);

    try {
      const cartItemsWithPrices = safeCart.map((item) => {
        const productDetails = safeSupplierData[item.ean];
        return {
          ean: item.ean,
          name: item.name,
          quantity: Number(item.quantity), // Ensure quantity is a Number, not a string
          price: productDetails?.netPrice || item.price,
          selling_price: productDetails?.sellingPrice || item.price,
        };
      });

      const baseNotes = `Kosár tranzakció - ${safeCart.length} termék`;
      const trimmedNote = transactionNote.trim();
      const finalNotes = trimmedNote ? `${baseNotes} | Note: ${trimmedNote}` : baseNotes;

      // 🔥 ATOMBIZTOSAN MŰKÖDŐ OFFLINE-FIRST CHECKOUT
      // forceOffline = true if offline OR backend unreachable
      const forceOffline = !networkStatus.isOnline || !networkStatus.isConnectedToBackend;

      const result = await supabaseService.processCheckoutBatch(
        selectedPaymentType,
        cartItemsWithPrices,
        finalNotes,
        identifiedSellerName || undefined,
        currentTerminalName,
        scanMode === 'RETURN' ? 'RETURN' : 'SALE',
        forceOffline,
        discountPercent > 0 ? discountPercent : undefined
      );

      // Update inventory state
      setInventoryData((prev) => {
        const updated = { ...prev };
        (result?.updated_items || []).forEach((item) => {
          if (!item?.ean) return;
          const { ean, new_quantity } = item;
          const existingItem = prev[ean];
          updated[ean] = {
            ean,
            quantity: new_quantity ?? 0,
            scannedAt: new Date(),
            min_quantity: existingItem?.min_quantity ?? 0,
            on_shopping_list: existingItem?.on_shopping_list ?? false,
          };
        });
        return updated;
      });

      // Save transaction for receipt (using cart_transaction_id from new schema)
      const receiptItems = safeCart.map((item) => {
        const supplierItem = safeSupplierData[item.ean];
        return {
          ean: item.ean,
          name: item.name,
          quantity: item.quantity,
          price: item.price, // Gross price
          total: item.price * item.quantity, // Gross total
          vatRate: supplierItem?.vatRate || 27, // VAT rate for this item
        };
      });
      const subtotal = safeCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const discountAmt = discountPercent > 0 ? Math.round(subtotal * discountPercent) / 100 : 0;
      const totalAmount = subtotal - discountAmt;
      setLastTransaction({
        items: receiptItems,
        totalAmount: totalAmount,
        paymentType: selectedPaymentType,
        transactionId: result.cart_transaction_id,
        date: new Date(),
        sellerName: identifiedSellerName || undefined,
        discountPercent: discountPercent > 0 ? discountPercent : undefined,
        discountAmount: discountAmt > 0 ? discountAmt : undefined,
      });

      // Log checkout transaction to inventory_logs
      const totalQuantity = safeCart.reduce((sum, item) => sum + item.quantity, 0);
      const productNames = safeCart.map((item) => item.name || 'Ismeretlen').join(', ');
      const paymentTypeText =
        selectedPaymentType === 'CASH'
          ? 'Készpénz'
          : selectedPaymentType === 'CARD'
            ? 'Kártya'
            : selectedPaymentType === 'INVOICE'
              ? 'Számla'
              : 'Egyéb';
      const sellerText = identifiedSellerName ? ` | Eladó: ${identifiedSellerName}` : '';
      const terminalText = currentTerminalName ? ` | Pult: ${currentTerminalName}` : '';
      const modeText = scanMode === 'RETURN' ? 'Visszáru' : 'Eladás';
      const noteText = trimmedNote ? ` | Note: ${trimmedNote}` : '';
      const quantitySign = scanMode === 'RETURN' ? '+' : '-';
      const discountText =
        discountPercent > 0 ? ` | Kedvezmény: ${discountPercent}% (-${discountAmt} Ft)` : '';
      const logDetails = `${modeText} tranzakció ${quantitySign} ${productNames}, ${quantitySign}${totalQuantity} db, Összeg: ${totalAmount} Ft, Fizetés: ${paymentTypeText}${discountText}${sellerText}${terminalText}${noteText}`;

      await supabaseService.logAction(
        scanMode === 'RETURN' ? 'RETURN' : 'SALE',
        logDetails,
        false,
        supabaseService.addToQueue.bind(supabaseService)
      );

      // Check if transaction was queued offline
      const isOfflineTransaction = result.cart_transaction_id.startsWith('offline-');

      if (isOfflineTransaction) {
        // 🔥 OFFLINE MODE: Show special warning toast
        toast(
          `📦 Tranzakció OFFLINE MÓDBAN mentve!\n\n` +
            `${safeCart.length} termék ideiglenesen elmentve.\n` +
            `Szinkronizálás automatikusan megtörténik, amikor a kapcsolat helyreáll.`,
          {
            duration: 8000,
            icon: '⚠️',
            style: {
              background: '#FF9800',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              lineHeight: '1.5',
            },
          }
        );
      } else {
        // Online success
        const successMessage =
          scanMode === 'RETURN'
            ? `Visszáru tranzakció sikeresen rögzítve! ${safeCart.length} termék feldolgozva.`
            : `Tranzakció sikeresen rögzítve! ${safeCart.length} termék feldolgozva.`;
        toast.success(successMessage);
      }
      playAudio('success');

      updateSavedState({
        cart: [],
        isCheckoutModalOpen: false,
        checkoutStep: 'SELECT_TYPE',
        discountPercent: 0,
      });
      setTransactionNote('');

      // Reset seller identification after successful checkout
      setIdentifiedSellerName('');

      returnFocusToMainInput();
    } catch (error: any) {
      console.error('[Checkout] ❌ Batch checkout failed:', error);
      const errorMessage = error?.message || getUserFriendlyError(error);
      toast.error(`Checkout hiba: ${errorMessage}`);
      playAudio('error');
    } finally {
      setIsProcessingCheckout(false);
      onCheckoutComplete?.();
    }
  }, [
    isProcessingCheckout,
    safeCart,
    scanMode,
    selectedPaymentType,
    discountPercent,
    safeSupplierData,
    setInventoryData,
    updateSavedState,
    playAudio,
    returnFocusToMainInput,
    onCheckoutStart,
    onCheckoutComplete,
    identifiedSellerName,
    currentTerminalName,
    handleInventoryCheckout,
    transactionNote,
  ]);

  // Suspend cart
  const handleSuspendCart = useCallback(async () => {
    if (safeCart.length === 0) return;
    setIsSuspendingCart(true);
    try {
      await supabaseService.suspendCart(safeCart, suspendNotes.trim() || undefined);
      toast.success(
        'Kosár sikeresen parkolva! Folytathatod később a "Parkolt kosarak" menüpontból.'
      );
      updateSavedState({ cart: [] });
      setTransactionNote('');
      setShowSuspendModal(false);
      setSuspendNotes('');
    } catch (error: any) {
      console.error('Error suspending cart:', error);
      toast.error(`Parkolás hiba: ${getUserFriendlyError(error)}`);
    } finally {
      setIsSuspendingCart(false);
    }
  }, [safeCart, suspendNotes, updateSavedState]);

  // Print receipt
  const handlePrintReceipt = useCallback(async () => {
    if (!lastTransaction) {
      toast.error('Nincs nyugta adat a nyomtatáshoz');
      return;
    }

    setIsPrinting(true);
    try {
      const { generateReceiptPDF } = await import('../services/pdfService');
      await generateReceiptPDF(lastTransaction);
      toast.success('Nyugta PDF sikeresen generálva');
    } catch (error: any) {
      console.error('[Receipt] Error generating PDF:', error);
      toast.error(`PDF generálási hiba: ${error?.message || 'Ismeretlen hiba'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [lastTransaction]);

  // Finalize pending adjustment (RETURN/SCRAP/INTERNAL)
  const handleFinalizeAdjustment = useCallback(async () => {
    if (!pendingAdjustment || !identifiedSellerName) {
      toast.error('Hiányzó adatok a véglegesítéshez');
      return;
    }

    const trimmedNote = transactionNote.trim();
    const noteText = trimmedNote ? ` | Note: ${trimmedNote}` : '';

    setIsProcessingCheckout(true);
    try {
      const result = await supabaseService.updateItemQuantity(
        pendingAdjustment.ean,
        pendingAdjustment.quantity,
        pendingAdjustment.productDetails,
        pendingAdjustment.mode,
        false,
        pendingAdjustment.oldQuantity,
        identifiedSellerName
      );

      if (result) {
        const newQty = result.new_quantity;
        setInventoryData((prev) => ({
          ...prev,
          [pendingAdjustment.ean]: {
            ean: pendingAdjustment.ean,
            quantity: newQty,
            scannedAt: new Date(result.updated_at),
            min_quantity: result.min_quantity ?? prev[pendingAdjustment.ean]?.min_quantity ?? 0,
            on_shopping_list:
              result.on_shopping_list ?? prev[pendingAdjustment.ean]?.on_shopping_list ?? false,
          },
        }));

        // Log the transaction
        const modeText =
          pendingAdjustment.mode === 'RETURN'
            ? 'Visszáru'
            : pendingAdjustment.mode === 'SCRAP'
              ? 'Selejt'
              : 'Saját felhasználás';
        const logDetails = `${modeText} tranzakció - ${pendingAdjustment.name} (${pendingAdjustment.quantity} ${pendingAdjustment.unit}) | Felhasználó: ${identifiedSellerName}${noteText}`;

        await supabaseService.logAction(
          pendingAdjustment.mode as ScanMode,
          logDetails,
          false,
          supabaseService.addToQueue.bind(supabaseService)
        );

        toast.success(`Készletmódosítás sikeresen véglegesítve!`);
        playAudio('success');

        // Clear pending adjustment and close modal
        setIdentifiedSellerName('');
        setTransactionNote('');
        updateSavedState({
          pendingAdjustment: null,
          isCheckoutModalOpen: false,
          checkoutStep: 'SELECT_TYPE',
          discountPercent: 0,
        });
        returnFocusToMainInput();
      } else {
        toast.error('Hiba a készletmódosítás véglegesítésénél');
        playAudio('error');
      }
    } catch (error: any) {
      console.error('[Finalize Adjustment] ❌ Failed:', error);
      const errorMessage = error?.message || getUserFriendlyError(error);
      toast.error(`Készletmódosítás hiba: ${errorMessage}`);
      playAudio('error');
    } finally {
      setIsProcessingCheckout(false);
    }
  }, [
    pendingAdjustment,
    identifiedSellerName,
    safeInventoryData,
    setInventoryData,
    playAudio,
    returnFocusToMainInput,
    updateSavedState,
    transactionNote,
  ]);

  // Confirm pending transactions (RETURN/SCRAP/INTERNAL)
  const handleConfirmTransactions = useCallback(async () => {
    if (pendingTransactions.length === 0) {
      toast.error('Nincs véglegesítendő tranzakció');
      return;
    }

    const trimmedNote = transactionNote.trim();
    const noteText = trimmedNote ? ` | Note: ${trimmedNote}` : '';

    setIsProcessingCheckout(true);
    try {
      // Process each pending transaction
      for (const transaction of pendingTransactions) {
        const productDetails = safeSupplierData[transaction.ean];
        if (!productDetails) {
          console.warn('Product details not found for EAN:', transaction.ean);
          continue;
        }

        const oldQty = safeInventoryData[transaction.ean]?.quantity ?? 0;
        const result = await supabaseService.updateItemQuantity(
          transaction.ean,
          transaction.quantity,
          productDetails,
          transaction.mode,
          false,
          oldQty
        );

        if (result) {
          const newQty = result.new_quantity;
          setInventoryData((prev) => ({
            ...prev,
            [transaction.ean]: {
              ean: transaction.ean,
              quantity: newQty,
              scannedAt: new Date(result.updated_at),
              min_quantity: result.min_quantity ?? prev[transaction.ean]?.min_quantity ?? 0,
              on_shopping_list:
                result.on_shopping_list ?? prev[transaction.ean]?.on_shopping_list ?? false,
            },
          }));
        }
      }

      // Log the transaction batch
      const modeText =
        pendingTransactions[0].mode === 'RETURN'
          ? 'Visszáru'
          : pendingTransactions[0].mode === 'SCRAP'
            ? 'Selejt'
            : 'Saját felhasználás';
      const logDetails = `${modeText} tranzakció - ${pendingTransactions.length} termék | Felhasználó: ${transactionUser}${noteText}`;

      await supabaseService.logAction(
        pendingTransactions[0].mode as ScanMode,
        logDetails,
        false,
        supabaseService.addToQueue.bind(supabaseService)
      );

      toast.success(
        `Tranzakció sikeresen véglegesítve! ${pendingTransactions.length} termék feldolgozva.`
      );
      playAudio('success');

      // Clear pending transactions and close modal
      setPendingTransactions([]);
      setShowTransactionConfirmationModal(false);
      setTransactionUser('');
      setTransactionNote('');
      returnFocusToMainInput();
    } catch (error: any) {
      console.error('[Transaction Confirmation] ❌ Failed:', error);
      const errorMessage = error?.message || getUserFriendlyError(error);
      toast.error(`Tranzakció hiba: ${errorMessage}`);
      playAudio('error');
    } finally {
      setIsProcessingCheckout(false);
    }
  }, [
    pendingTransactions,
    transactionUser,
    safeSupplierData,
    safeInventoryData,
    setInventoryData,
    playAudio,
    returnFocusToMainInput,
    transactionNote,
  ]);

  // Product data for UI
  const lastItemDetails = lastScanned ? safeSupplierData[lastScanned] : null;
  const lastItemQty = lastScanned ? safeInventoryData[lastScanned]?.quantity : 0;
  const isKnown = !!lastItemDetails;

  // Recent scans calculation - memoized to prevent expensive re-sorting
  const recentScans = useMemo(() => {
    return Object.values(safeInventoryData || {})
      .filter((item) => item?.scannedAt != null)
      .sort((a, b) => {
        try {
          const timeA = a.scannedAt ? new Date(a.scannedAt).getTime() : 0;
          const timeB = b.scannedAt ? new Date(b.scannedAt).getTime() : 0;
          if (isNaN(timeA) || isNaN(timeB)) return 0;
          return timeB - timeA;
        } catch {
          return 0;
        }
      })
      .slice(0, 5);
  }, [safeInventoryData]);

  // Handle Enter key - check if ProductCard is visible first
  const handleKeyDownMain = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();

        // If ProductCard is visible (lastScanned exists), call handleApprove instead of handleScan
        if (lastScanned && !outOfStockError) {
          const productDetails = safeSupplierData[lastScanned];
          const isKnown = !!productDetails;

          if (isKnown) {
            handleApprove();
          } else {
            handleSaveNewProduct();
          }
          return;
        }

        // Normal scan flow when ProductCard is not visible
        handleScan(isRapidMode);
      }
    },
    [
      lastScanned,
      outOfStockError,
      safeSupplierData,
      scanMode,
      isRapidMode,
      handleApprove,
      handleSaveNewProduct,
      handleScan,
    ]
  );

  return {
    // State
    barcodeInput,
    lastScanned,
    newName,
    newPrice,
    newSellingPrice,
    newPurchaseNet,
    newPurchaseGross,
    newSellingNet,
    newSellingGross,
    newVatRate,
    newMinQuantity,
    newIsWeighable,
    newUnitWeight,
    newUnit,
    scanMode,
    transactionQty,
    isMuted,
    isRapidMode,
    outOfStockError,
    isProcessingCheckout,
    lastTransaction,
    isPrinting,
    isSuspendingCart,
    showSuspendModal,
    suspendNotes,
    transactionNote,
    nameSuggestions,
    showSuggestions,
    recentTransactions,
    recentScans,
    safeCart,
    isCheckoutModalOpen,
    checkoutStep,
    selectedPaymentType,
    discountPercent,
    identifiedSellerName,
    lastItemDetails,
    lastItemQty,
    isKnown,
    safeAllowedModes,

    // Refs
    mainInputRef,
    nameInputRef,
    qtyInputRef,

    // Actions
    updateSavedState,
    updateNewUnit,
    handleScan,
    handleKeyDownMain,
    handleQtyChange,
    handleSaveNewProduct,
    handleApprove,
    handleDiscard,
    handleGenerateInternalEan,
    handleNameChange,
    handleCheckout,
    handleSuspendCart,
    handlePrintReceipt,
    toggleMute,
    toggleRapidMode,
    setScanMode: (mode: ScanMode) => updateSavedState({ scanMode: mode }),
    setOutOfStockError,
    setShowSuspendModal,
    setSuspendNotes,
    setTransactionNote,
    setShowSuggestions,
    setLastTransaction,
    setNameSuggestions,
    returnFocusToMainInput,

    // Weight modal state and handlers
    showWeightModal,
    pendingWeighableItem,
    handleWeightConfirm,
    setShowWeightModal,

    // Transaction confirmation state and handlers
    pendingTransactions,
    showTransactionConfirmationModal,
    transactionUser,
    handleConfirmTransactions,
    setShowTransactionConfirmationModal,

    // Adjustment confirmation state and handlers
    pendingAdjustment,
    handleFinalizeAdjustment,
  };
};
